/* =============================================================
   StudentGlobalSearch.jsx — Student portal header search bar
   -------------------------------------------------------------
   Searches across portal pages (sections), document names, and
   discount brands. Separate from the admin GlobalSearch because
   student routes and data categories are different.

   Categories searched:
   - SECTIONS: portal pages navigable from the header
   - DOCS: document types the student needs to upload
   - DISCOUNTS: partner brands in the Discounts section
   ============================================================= */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/* ─── SEARCHABLE DATA ───────────────────────────────────── */
const SECTIONS = [
  { label: 'My Dashboard',          icon: 'dashboard',          path: '/student/dashboard'  },
  { label: 'My Documents',          icon: 'folder_open',        path: '/student/documents'  },
  { label: 'Learning Agreement',    icon: 'assignment',         path: '/student/learning-agreement' },
  { label: 'My University',         icon: 'school',             path: '/student/university' },
  { label: 'Student Experiences',   icon: 'explore',            path: '/student/experiences' },
  { label: 'Discounts & Deals',     icon: 'local_offer',        path: '/student/discounts'  },
  { label: 'AI Assistant',          icon: 'smart_toy',          path: '/student/ai'         },
  { label: 'Settings',              icon: 'settings',           path: '/student/settings'   },
];

/* Document names the student may search for               */
const DOCS = [
  'Passport copy',
  'Health Insurance',
  'Housing Confirmation',
  'Pre-departure Form',
  'Visa Application',
  'Learning Agreement',
  'Transcript Request',
];

/* Discount brands searchable from the header              */
const DISCOUNTS = [
  { brand: 'HousingAnywhere', category: 'Accommodation', deal: '3 weeks free rent guarantee'   },
  { brand: 'AXA Travel Insurance', category: 'Insurance', deal: '15% off student exchange plans' },
  { brand: 'Wise',            category: 'Finance',       deal: 'Free transfer up to £500'       },
  { brand: 'Revolut',         category: 'Finance',       deal: '3 months Premium free'          },
  { brand: 'Airalo',          category: 'Connectivity',  deal: '10% off eSIM data plans'        },
  { brand: 'Eurostar',        category: 'Travel',        deal: '20% off London–Paris trips'     },
  { brand: 'Omio',            category: 'Travel',        deal: '10% off European routes'        },
  { brand: '16-25 Railcard',  category: 'Travel',        deal: '1/3 off UK rail fares'          },
];

/* ─── STUDENT GLOBAL SEARCH COMPONENT ──────────────────── */

function highlight(text, query) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-indigo-100 text-indigo-700 rounded px-0.5 not-italic font-semibold">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export default function StudentGlobalSearch() {
  const [query,   setQuery]   = useState('');
  const [open,    setOpen]    = useState(false);
  const [results, setResults] = useState({ sections: [], docs: [], discounts: [] });
  const navigate = useNavigate();
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);

  /* ── Compute results whenever query changes ── */
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) { setResults({ sections: [], docs: [], discounts: [] }); return; }

    setResults({
      sections:  SECTIONS.filter(s =>
        s.label.toLowerCase().includes(q)
      ).slice(0, 3),

      docs: DOCS.filter(d =>
        d.toLowerCase().includes(q)
      ).slice(0, 4),

      discounts: DISCOUNTS.filter(d =>
        d.brand.toLowerCase().includes(q) ||
        d.category.toLowerCase().includes(q) ||
        d.deal.toLowerCase().includes(q)
      ).slice(0, 3),
    });
  }, [query]);

  /* ── Close on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── Close on ESC ── */
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const hasResults =
    results.sections.length > 0 ||
    results.docs.length > 0 ||
    results.discounts.length > 0;

  const noResults = query.trim().length >= 2 && !hasResults;

  const go = (path) => {
    navigate(path);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={wrapRef} className="relative flex-1 max-w-sm">
      {/* Input */}
      <div className="relative">
        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search documents, courses..."
          className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <span className="material-symbols-outlined text-[16px]">close</span>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && query.trim().length >= 1 && (
        <div className="absolute top-full mt-2 left-0 w-[380px] bg-white border border-slate-100 rounded-2xl shadow-xl shadow-slate-200/60 z-50 overflow-hidden">

          {noResults && (
            <div className="px-5 py-8 text-center">
              <span className="material-symbols-outlined text-slate-300 text-[32px]">search_off</span>
              <p className="text-sm text-slate-400 mt-2">No results for "<span className="font-medium text-slate-500">{query}</span>"</p>
            </div>
          )}

          {/* Sections */}
          {results.sections.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Pages</p>
              {results.sections.map((s, i) => (
                <button
                  key={i}
                  onClick={() => go(s.path)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 transition-colors text-left group"
                >
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <span className="material-symbols-outlined text-indigo-500 text-[18px]">{s.icon}</span>
                  </div>
                  <p className="text-sm font-medium text-[#0b1c30] flex-1">
                    {highlight(s.label, query)}
                  </p>
                  <span className="material-symbols-outlined text-slate-300 group-hover:text-indigo-400 text-[16px] flex-shrink-0 transition-colors">arrow_forward</span>
                </button>
              ))}
            </div>
          )}

          {/* Documents */}
          {results.docs.length > 0 && (
            <div className={results.sections.length > 0 ? 'border-t border-slate-50' : ''}>
              <p className="px-4 pt-3 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Documents</p>
              {results.docs.map((d, i) => (
                <button
                  key={i}
                  onClick={() => go('/student/documents')}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 transition-colors text-left group"
                >
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <span className="material-symbols-outlined text-slate-500 text-[18px]">description</span>
                  </div>
                  <p className="text-sm font-medium text-[#0b1c30] flex-1">
                    {highlight(d, query)}
                  </p>
                  <span className="material-symbols-outlined text-slate-300 group-hover:text-indigo-400 text-[16px] flex-shrink-0 transition-colors">arrow_forward</span>
                </button>
              ))}
            </div>
          )}

          {/* Discounts */}
          {results.discounts.length > 0 && (
            <div className={(results.sections.length > 0 || results.docs.length > 0) ? 'border-t border-slate-50' : ''}>
              <p className="px-4 pt-3 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Discounts & Deals</p>
              {results.discounts.map((d, i) => (
                <button
                  key={i}
                  onClick={() => go('/student/discounts')}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 transition-colors text-left group"
                >
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <span className="material-symbols-outlined text-amber-500 text-[18px]">local_offer</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#0b1c30] leading-tight">
                      {highlight(d.brand, query)}
                    </p>
                    <p className="text-xs text-slate-400 truncate">
                      {d.category} · {d.deal}
                    </p>
                  </div>
                  <span className="material-symbols-outlined text-slate-300 group-hover:text-indigo-400 text-[16px] flex-shrink-0 transition-colors">arrow_forward</span>
                </button>
              ))}
            </div>
          )}

          {/* Footer hint */}
          {hasResults && (
            <div className="px-4 py-2 border-t border-slate-50 flex items-center gap-1">
              <span className="text-[10px] text-slate-300">Press</span>
              <kbd className="text-[10px] bg-slate-100 text-slate-400 px-1 py-0.5 rounded font-mono">ESC</kbd>
              <span className="text-[10px] text-slate-300">to close</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
